const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export const isSameDay = (left, right) => (
	left.getFullYear() === right.getFullYear()
	&& left.getMonth() === right.getMonth()
	&& left.getDate() === right.getDate()
)

export const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate())

export const toDateKey = (date) => {
	const year = date.getFullYear()
	const month = String(date.getMonth() + 1).padStart(2, '0')
	const day = String(date.getDate()).padStart(2, '0')

	return `${year}-${month}-${day}`
}

export const parseDateKey = (dateKey) => {
	if (dateKey instanceof Date) {
		return startOfDay(dateKey)
	}

	const [year, month, day] = String(dateKey).split('-').map(Number)
	return new Date(year, month - 1, day)
}

export const getTimelineQueryRange = (anchorDate) => {
	const from = new Date(anchorDate.getFullYear(), anchorDate.getMonth() - 2, 1)
	const to = new Date(anchorDate.getFullYear(), anchorDate.getMonth() + 4, 0)

	return {
		date_from: toDateKey(from),
		date_to: toDateKey(to),
	}
}

export const getUpcomingQueryRange = (anchorDate, limit = 25, offset = 0) => {
	const from = startOfDay(anchorDate)
	const to = new Date(anchorDate.getFullYear(), anchorDate.getMonth() + 6, 0)

	return {
		date_from: toDateKey(from),
		date_to: toDateKey(to),
		limit,
		offset,
	}
}

export const formatTimelineDateHeader = (date) => ({
	year: String(date.getFullYear()),
	dayMonth: `${date.getDate()} ${MONTH_SHORT[date.getMonth()]}`,
})

export const getUniqueEventDates = (events) => {
	const datesByKey = new Map()

	events.forEach((event) => {
		const eventDate = event.date instanceof Date ? event.date : new Date(event.date)
		const key = toDateKey(eventDate)

		if (!datesByKey.has(key)) {
			datesByKey.set(key, startOfDay(eventDate))
		}
	})

	return Array.from(datesByKey.values()).sort((left, right) => left.getTime() - right.getTime())
}

export const getTimelineDatesFromAnchor = (anchorDate, events, limit = 6) => {
	const eventDates = getUniqueEventDates(events)
	const anchor = startOfDay(anchorDate)
	const anchorIndex = eventDates.findIndex((date) => isSameDay(date, anchor))

	if (anchorIndex === -1) {
		const upcomingDates = eventDates.filter((date) => date.getTime() >= anchor.getTime())
		return upcomingDates.slice(0, limit)
	}

	return eventDates.slice(anchorIndex, anchorIndex + limit)
}

export const getAdjacentEventDate = (anchorDate, events, direction) => {
	const eventDates = getUniqueEventDates(events)
	const anchor = startOfDay(anchorDate)
	const anchorIndex = eventDates.findIndex((date) => isSameDay(date, anchor))

	if (direction === 'previous') {
		if (anchorIndex > 0) {
			return eventDates[anchorIndex - 1]
		}

		const previousDates = eventDates.filter((date) => date.getTime() < anchor.getTime())
		return previousDates.at(-1) ?? null
	}

	if (direction === 'next') {
		if (anchorIndex !== -1 && anchorIndex < eventDates.length - 1) {
			return eventDates[anchorIndex + 1]
		}

		const nextDates = eventDates.filter((date) => date.getTime() > anchor.getTime())
		return nextDates[0] ?? null
	}

	return null
}

export const formatProgressChangedAt = (value) => {
	if (!value) {
		return '—'
	}

	const date = new Date(value)
	if (Number.isNaN(date.getTime())) {
		return '—'
	}

	const hours = String(date.getHours()).padStart(2, '0')
	const minutes = String(date.getMinutes()).padStart(2, '0')

	return `${date.getDate()} ${MONTH_SHORT[date.getMonth()]} ${date.getFullYear()}, ${hours}:${minutes}`
}
